import { useEffect, useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useDarkMode, api } from '@halo/web-lib';
import {
  LayoutDashboard,
  Store,
  Users,
  Database,
  Workflow,
  Activity,
  LogOut,
  Building2,
  KeyRound,
  Wrench,
  Sparkles,
  FlaskConical,
  MessageSquare,
  LineChart,
  AlertTriangle,
  ShieldAlert,
  Sun,
  Moon,
  Calendar,
  Briefcase,
  ListChecks,
  HeartPulse,
  Gauge,
  ToggleRight,
  BellRing,
  Power,
  ChevronDown,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Tenants',
    items: [
      { to: '/agency', label: 'Agency Clients', icon: Building2 },
      { to: '/stores', label: 'Stores', icon: Store },
      { to: '/users', label: 'Users', icon: Users },
      { to: '/api-keys', label: 'API Keys', icon: KeyRound },
    ],
  },
  {
    label: 'Pipeline',
    items: [
      { to: '/ingestion', label: 'Ingestion', icon: Database },
      { to: '/scheduler', label: 'Scheduler', icon: Calendar },
      { to: '/cloud-run-jobs', label: 'Cloud Run Jobs', icon: Briefcase },
      { to: '/cloud-tasks', label: 'Cloud Tasks', icon: ListChecks },
      { to: '/sla', label: 'Data SLA', icon: HeartPulse },
      { to: '/data-ops', label: 'Data Ops', icon: Gauge },
      { to: '/shopify-parity', label: 'Shopify Parity', icon: LineChart },
      { to: '/shopify-diagnostic', label: 'Shopify Diagnostic', icon: ShieldCheck },
      { to: '/connection-health', label: 'Connection Health', icon: HeartPulse },
      { to: '/halo-mmm', label: 'HaloMMM', icon: Sparkles },
      { to: '/halo-v2', label: 'HaloV2 Verify', icon: FlaskConical },
    ],
  },
  {
    label: 'Jobs',
    items: [
      { to: '/jobs', label: 'Jobs', icon: Workflow },
      { to: '/failed-jobs', label: 'Failed Jobs', icon: AlertTriangle },
      { to: '/circuit-breakers', label: 'Circuit Breakers', icon: ShieldAlert },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/slack', label: 'Slack', icon: MessageSquare },
      { to: '/feature-flags', label: 'Feature Flags', icon: ToggleRight },
      { to: '/alerts', label: 'Alerts', icon: BellRing },
      { to: '/anomalies', label: 'Anomalies', icon: AlertTriangle },
      { to: '/monitoring', label: 'Monitoring', icon: Activity },
      { to: '/services', label: 'Services', icon: Activity },
      { to: '/operations', label: 'Operations', icon: Wrench },
      { to: '/maintenance', label: 'Maintenance', icon: Power },
    ],
  },
];

interface DriftBannerData {
  storeCount: number;
  stores: Array<{ storeId: number; driftedMetrics: number }>;
}

function DriftBanner() {
  const { data } = useQuery({
    queryKey: ['shopify-parity', 'drift-banner'],
    queryFn: async ({ signal }) => {
      const res = await api.get<DriftBannerData>(
        '/api/v1/ingestion/admin/shopify-parity/drift-banner',
        { signal },
      );
      if (!res.success || !res.data) return { storeCount: 0, stores: [] };
      return res.data;
    },
    refetchInterval: 120_000,
  });

  if (!data || data.storeCount === 0) return null;

  return (
    <Link
      to="/shopify-parity"
      className="block bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900/50 px-5 py-2.5 hover:bg-amber-100 dark:hover:bg-amber-950/60 transition-colors"
    >
      <div className="max-w-[1400px] mx-auto flex items-center gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0" />
        <span className="text-amber-900 dark:text-amber-200 font-medium">
          {data.storeCount} Shopify store{data.storeCount === 1 ? '' : 's'} drifting in the last 24h
        </span>
        <span className="text-amber-700 dark:text-amber-400">
          (
          {data.stores
            .slice(0, 3)
            .map((s) => `#${s.storeId}`)
            .join(', ')}
          {data.stores.length > 3 ? `, +${data.stores.length - 3} more` : ''})
        </span>
        <span className="ml-auto text-xs text-amber-700 dark:text-amber-400">
          View parity dashboard →
        </span>
      </div>
    </Link>
  );
}

/** Halo mark — yellow disc with the signal wave. Matches the design-system logo. */
function HaloMark({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="14" cy="14" r="14" fill="#FFC712" />
      <rect x="6.50444" y="15.59" width="1.81724" height="3.18017" rx="0.908619" fill="white" />
      <rect x="10.6842" y="11.7738" width="1.81724" height="6.99637" rx="0.908619" fill="white" />
      <path
        d="M14.682 5.18637C22.9311 8.18814 22.9117 19.8844 14.6366 22.8136"
        stroke="white"
        strokeWidth="1.81724"
        strokeLinecap="round"
      />
      <path
        d="M14.3646 9.63855L15.1136 10.3875C17.1979 12.4719 17.1675 15.8604 15.0461 17.907"
        stroke="white"
        strokeWidth="1.81724"
        strokeLinecap="round"
      />
    </svg>
  );
}

function initialsOf(email?: string | null): string {
  if (!email) return '?';
  const [local] = email.split('@');
  if (!local) return '?';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

const COLLAPSED_STORAGE_KEY = 'halo.admin.sidebar.collapsedSections';

function loadCollapsedSections(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed))
      return new Set(parsed.filter((s): s is string => typeof s === 'string'));
  } catch {
    /* ignore */
  }
  return new Set();
}

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();

  // All sections open by default; user can collapse any and we persist their
  // choice. Stored as the set of *collapsed* labels so the default (everything
  // expanded) is just an empty set.
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsedSections);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...collapsed]));
    } catch {
      /* ignore quota / private-mode */
    }
  }, [collapsed]);

  const toggleSection = (label: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleLogout = () => {
    void logout();
    navigate('/login');
  };

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-950 overflow-hidden">
      {/* Sidebar — full viewport height, only the nav scrolls. */}
      <aside className="w-52 h-screen bg-charcoalGray-900 text-gray-300 flex flex-col shrink-0">
        {/* Brand */}
        <div className="px-4 h-14 flex items-center gap-2.5 border-b border-white/5 shrink-0">
          <HaloMark size={22} />
          <div className="flex items-baseline gap-1.5">
            <span className="text-[13px] font-semibold tracking-tight text-white">Halo</span>
            <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-gray-500">
              Admin
            </span>
          </div>
        </div>

        {/* Nav — the only scrollable region */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-3 px-2">
          {NAV_SECTIONS.map((section, idx) => {
            const isCollapsed = collapsed.has(section.label);
            return (
              <div key={section.label} className={idx === 0 ? '' : 'mt-3'}>
                <button
                  type="button"
                  onClick={() => toggleSection(section.label)}
                  aria-expanded={!isCollapsed}
                  className="group w-full flex items-center justify-between px-2 mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <span>{section.label}</span>
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'}`}
                    aria-hidden
                  />
                </button>
                {!isCollapsed && (
                  <div className="space-y-px">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/'}
                        className={({ isActive }) =>
                          `group relative flex items-center gap-2.5 pl-3 pr-2 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                            isActive
                              ? 'bg-white/[0.06] text-white'
                              : 'text-gray-400 hover:text-white hover:bg-white/[0.03]'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {/* Active indicator bar */}
                            <span
                              aria-hidden
                              className={`absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full transition-opacity ${
                                isActive ? 'bg-[#FFC712] opacity-100' : 'opacity-0'
                              }`}
                            />
                            <item.icon
                              className={`w-3.5 h-3.5 shrink-0 transition-colors ${
                                isActive
                                  ? 'text-[#FFC712]'
                                  : 'text-gray-500 group-hover:text-gray-300'
                              }`}
                            />
                            <span className="truncate">{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer: theme toggle + user pill — pinned at the bottom */}
        <div className="border-t border-white/5 p-2 space-y-1 shrink-0">
          <button
            onClick={toggleDarkMode}
            className="flex items-center gap-2.5 pl-3 pr-2 py-1.5 w-full text-[13px] font-medium text-gray-400 hover:text-white rounded-md hover:bg-white/[0.03] transition-colors"
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <Sun className="w-3.5 h-3.5 text-gray-500" />
            ) : (
              <Moon className="w-3.5 h-3.5 text-gray-500" />
            )}
            {isDarkMode ? 'Light mode' : 'Dark mode'}
          </button>

          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md">
            <div className="w-6 h-6 rounded-full bg-[#FFC712] text-charcoalGray-900 text-[10px] font-bold flex items-center justify-center shrink-0">
              {initialsOf(user?.email)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] text-gray-300 truncate" title={user?.email ?? ''}>
                {user?.email ?? '—'}
              </div>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              aria-label="Sign out"
              className="p-1 rounded-md text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <DriftBanner />
        <div className="max-w-[1400px] mx-auto px-5 py-5">{children}</div>
      </main>
    </div>
  );
}
